<script setup lang="ts">
import AppointmentStatusContent from '@/components/manage-appointment/AppointmentStatusContent.vue';
import AppointmentStatusPayButton from '@/components/manage-appointment/AppointmentStatusPayButton.vue';
import AppointmentStatusTitle from '@/components/manage-appointment/AppointmentStatusTitle.vue';
import { Button } from '@/components/ui/button';
import { AppointmentStatusV2 } from '@/lib/status-v2';
import { formatDeadline, getParam } from '@/lib/utils';
import { store } from '@/store/store';
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import Boldable from '../shared/Boldable.vue';

const props = defineProps<{
  status: AppointmentStatusV2;
}>();

const route = useRoute();

function formatHour(date: string) {
  return new Date(date).toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
}

const isPastVisitTime = computed(() => props.status.status === 'confirmed' && new Date(props.status.meta.before_visit_time) < new Date());

const statusInfo = computed(() => props.status);

defineOptions({ name: 'AppointmentStatus' });
</script>

<template>
  <div class="border-t border-gray-200 p-5 grid gap-2">
    <template v-if="statusInfo.status === 'confirmed'">
      <AppointmentStatusTitle class="text-secondary">
        {{ isPastVisitTime ? "Doctor hasn't called yet?" : 'Waiting for Your Appointment' }}
      </AppointmentStatusTitle>
      <!-- content for in-person appointments -->
      <AppointmentStatusContent v-if="statusInfo.meta.is_in_person">
        Please arrive at least 15 minutes before your scheduled visit to ensure your spot.
        <br />
        <br />
        Address:
        <a class="font-bold underline" href="https://share.google/G15Q6JqJ25jqch1ZC" target="_blank">
          {{ store.in_person_address }}
        </a>
      </AppointmentStatusContent>

      <!-- content for virtual appointments -->
      <AppointmentStatusContent v-else-if="isPastVisitTime">
        If the doctor hasn’t called yet, please don’t worry. they’ll contact you shortly from this number:
        <span class="font-bold inline-block">{{ store.doctor_phone }}.</span>
        <br />
        <br />
        We apologize for the delay and appreciate your patience.
      </AppointmentStatusContent>

      <AppointmentStatusContent v-else>
        The doctor will call you between
        <span class="font-bold inline-block">{{ formatHour(statusInfo.meta.before_visit_time) }}</span>
        and
        <span class="inline-block">
          <span class="font-bold">
            {{ formatHour(statusInfo.meta.after_visit_time) }}
          </span> (Pacific Time)
        </span>
        from this number: <span class="font-bold inline-block">{{ store.doctor_phone }}.</span>
      </AppointmentStatusContent>
    </template>

    <template v-else-if="statusInfo.status === 'reservation_expired'">
      <AppointmentStatusTitle class="text-red-700">
        Reservation Expired
      </AppointmentStatusTitle>
      <AppointmentStatusContent>
        The time to confirm your appointment has passed.
      </AppointmentStatusContent>
    </template>

    <template v-else-if="statusInfo.status === 'no_show'">
      <AppointmentStatusTitle class="text-red-700">
        Missed Appointment
      </AppointmentStatusTitle>
      <AppointmentStatusContent>
        It seems you missed your appointment. You can easily book another appointment.
      </AppointmentStatusContent>
    </template>

    <template v-else-if="statusInfo.status === 'visited'">
      <AppointmentStatusTitle class="text-emerald-700">
        Appointment Completed
      </AppointmentStatusTitle>
      <AppointmentStatusContent>
        We hope your appointment met your expectations.
      </AppointmentStatusContent>
    </template>

    <template v-else-if="statusInfo.status === 'waiting_for_payment'">
      <AppointmentStatusTitle class="text-orange-600">
        Reservation Pending Payment!
      </AppointmentStatusTitle>
      <AppointmentStatusContent>
         Your appointment is reserved. To confirm it, please pay
        <span class="font-bold inline-block">${{ statusInfo.meta.amount }} CAD</span>
        &nbsp;
        <Boldable class="inline-block" :text="formatDeadline(statusInfo.meta.deadline)" />.
        <AppointmentStatusPayButton />
      </AppointmentStatusContent>
    </template>

    <template v-else-if="statusInfo.status === 'changed'">
      <AppointmentStatusTitle class="text-orange-600">
        Appointment Rescheduled
      </AppointmentStatusTitle>
      <AppointmentStatusContent>
        Your appointment was cancelled, and a new one has been scheduled for you.
        <RouterLink class="mt-4 block"
          :to="{ name: 'manage_appointment', params: { domain: getParam(route) }, query: { appID: statusInfo.meta.alternative_app_id } }">
          <Button size="lg" class="w-full !text-lg font-semibold" variant="default">
            Open New Appointment
          </Button>
        </RouterLink>
      </AppointmentStatusContent>
    </template>

    <template v-else-if="statusInfo.status === 'cancelled_by_patient'">
      <AppointmentStatusTitle class="text-red-700">
        Appointment Cancelled
      </AppointmentStatusTitle>
      <AppointmentStatusContent>
        Your appointment was successfully cancelled.
      </AppointmentStatusContent>
    </template>

    <template v-else-if="statusInfo.status === 'cancelled_by_doctor'">
      <AppointmentStatusTitle class="text-red-700">
        Cancelled by Doctor
      </AppointmentStatusTitle>
      <AppointmentStatusContent>
        <template v-if="statusInfo.meta.reason === 'physical-exam-required'">
          After reviewing your case, the doctor determined that your condition requires a hands-on physical examination
          or procedure that cannot be performed through Telemedicine. An in-person visit is recommended.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'continuity-of-care'">
          The doctor believes your care will be best managed by your family physician, who is familiar with your medical
          history and ongoing treatment plan.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'controlled-medication-refill'">
          This prescription involves a controlled or narcotic medication, which by law cannot be renewed through
          Telemedicine. Please visit a clinic in person for this request.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'referral-or-sick-note'">
          The doctor has determined that your request cannot be fulfilled and that a referral or medical note should be
          provided through a different process.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'patient-declined-or-double-booked'">
          The appointment could not proceed because it was declined or there was an unintentional double-booking. If
          necessary, please
          rebook at your convenience.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'urgent-medical-attention'">
          Based on your symptoms, the doctor recommends urgent in-person care to ensure your safety and timely
          treatment.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'needs-physical-assessment'">
          Your symptoms require direct physical assessment, such as checking vital signs, reflexes, or other in-person
          evaluations, which cannot be done virtually.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'incorrect-patient-information'">
          We were unable to proceed because the patient information provided did not match our records. Please update
          your details and rebook.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'needs-refill-for-controlled-medication'">
          Your medication refill request involves a controlled or narcotic medication that must be prescribed in person
          in accordance with regulations.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'work-related-injury'">
          This case involves a work-related injury that must be assessed in person to meet workplace injury board
          requirements.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'patient-said-does-not-need-the-appointment'">
          The appointment has been cancelled as you indicated you no longer need it. You may rebook at any time if your
          situation changes.
        </template>
        <template v-else-if="statusInfo.meta.reason === 'needs-a-referral'">
          The doctor has determined that your request for a referral cannot be completed and should be arranged through
          a different process.
        </template>
        <template v-else>
          After careful consideration, doctor concluded that we can't go through with this appointment.
        </template>
      </AppointmentStatusContent>
    </template>

    <template v-else>
      <AppointmentStatusTitle class="text-red-700">
        Invalid Appointment
      </AppointmentStatusTitle>
      <AppointmentStatusContent>
        The appointment is invalid. Please contact support.
      </AppointmentStatusContent>
    </template>
  </div>
</template>